import { startTransition, useOptimistic } from 'react';
import { useSearchParams } from 'next/navigation';

import type {
  GTMEventGlycerinSort,
  GenericTrackingEvent,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { UseSortingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseSortingFragmentFragmentDoc';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { decodeOrder, encodeOrder } from '../queryEncoding';
import { getGlycerinSortMethod } from '../utils/tracking/getGlycerinSortMethod';
import { fewerProductsPerPage, filtersV2 } from '../activeFeatureFlags';
import { getTestId } from '../utils/featureFlags/getTestId';

/* GraphQL */ `
  fragment UseSortingFragment on SearchFilterableAndSortableResult {
    ...GetGlycerinSortMethodFragment
    defaultSortingKey
  }
`;

/**
 * Use sorting option persisted in the url query
 *
 * Changing sorting results in a router.push, which will trigger a data reload
 */
export const useSorting = (maskedData: FragmentType<typeof UseSortingFragmentFragmentDoc>) => {
  const result = unmask<typeof UseSortingFragmentFragmentDoc>(maskedData);

  const dispatchGtmEvent = useTracking();
  const { setOutcome } = useAbTesting();

  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const query = searchParamsToObject(searchParams);
  const sortingKey = decodeOrder(query) ?? result.defaultSortingKey;
  const [optimisticSortingKey, setOptimisticSortingKey] = useOptimistic(sortingKey);

  const pushSortingChange = (newOrder: string) => {
    if (newOrder === sortingKey) return;

    startTransition(() => setOptimisticSortingKey(newOrder));
    // default sorting key should not be visible in the URL
    const cleanSortingKey = newOrder === result.defaultSortingKey ? undefined : newOrder;
    pushQueryChange(encodeOrder(cleanSortingKey));

    setOutcome(getTestId(fewerProductsPerPage), { SORT: 1 });
    setOutcome(getTestId(filtersV2), { SORT: 1 });

    dispatchGtmEvent<GTMEventGlycerinSort>({
      event: 'Sort',
      SortData: {
        sortMethod: getGlycerinSortMethod(result, newOrder),
      },
    });

    dispatchGtmEvent<GenericTrackingEvent>({
      event: 'ga-event',
      eventAction: 'Filter / Sort benutzt',
      eventCategory: 'User Interactions',
      eventLabel: 'sortiert',
    });
  };

  return {
    sortingKey: optimisticSortingKey,
    pushSortingChange,
  };
};
